import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  getDailySettings, saveBrandProfile, saveDailySettings, runDailyNow, getDailyHistory, mediaUrl,
  uploadImage, previewDailyConcept, approveDailyConcept, discardDailyConcept,
  getDailyCalendar, planDailyCalendar, approveCalendarEntry, regenerateCalendarEntry, deleteCalendarEntry,
  approveAllCalendar, previewAssetScene, getCalendarStatus, dailyAutofill, listVideos,
} from "../lib/api";

// All Daily Fresh Video state, data-loading and handlers. Behavior-identical to the
// previous in-component logic; names are preserved so the page JSX is unchanged.
export function useDailyStudio() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isPro, setIsPro] = useState(false);
  const [options, setOptions] = useState(null);
  const [profile, setProfile] = useState(null);
  const [settings, setSettings] = useState(null);
  const [presets, setPresets] = useState([]);
  const [canTwiceDaily, setCanTwiceDaily] = useState(false);
  const [pastVideos, setPastVideos] = useState([]);
  const [autofilling, setAutofilling] = useState(false);
  const [remixTarget, setRemixTarget] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [running, setRunning] = useState(false);
  const [history, setHistory] = useState([]);
  const [uploadingAsset, setUploadingAsset] = useState(false);
  const [pending, setPending] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [approving, setApproving] = useState(false);
  const [calendar, setCalendar] = useState([]);
  const [planning, setPlanning] = useState(false);
  const [busyEntry, setBusyEntry] = useState(null);
  const [approvingAll, setApprovingAll] = useState(false);
  const [assetPreview, setAssetPreview] = useState(null);   // {url} shown in modal
  const [previewAsset, setPreviewAsset] = useState(null);   // path currently generating

  // Load everything once on mount.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const d = await getDailySettings();
        const prof = d.profile || {};
        // migrate legacy asset_paths -> assets objects
        if ((!prof.assets || !prof.assets.length) && (prof.asset_paths || []).length) {
          prof.assets = prof.asset_paths.map((p) => ({ path: p, role: "product" }));
        }
        prof.assets = prof.assets || [];
        if (!active) return;
        setOptions(d.options); setProfile(prof); setSettings(d.settings); setIsPro(d.is_pro); setPresets(d.presets || []); setCanTwiceDaily(!!d.can_twice_daily);
        listVideos().then((vs) => setPastVideos((vs || []).filter((v) => v.status === "completed").slice(0, 12))).catch(() => {});
        setPending(d.pending || null);
        const [h, cal] = await Promise.all([getDailyHistory().catch(() => []), getDailyCalendar().catch(() => [])]);
        if (!active) return;
        setHistory(h); setCalendar(cal);
      } catch { toast.error("Failed to load Daily Video settings"); }
      if (active) setLoading(false);
    })();
    return () => { active = false; };
  }, []);

  const setP = (k, v) => setProfile((p) => ({ ...p, [k]: v }));
  const setS = (k, v) => setSettings((s) => ({ ...s, [k]: v }));

  const handleAssetUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingAsset(true);
    try {
      const added = [];
      for (const f of files) { const res = await uploadImage(f); added.push({ path: res.path, role: "product" }); }
      setProfile((p) => ({ ...p, assets: [...(p.assets || []), ...added] }));
      toast.success(files.length > 1 ? "Assets added" : "Asset added");
    } catch { toast.error("Upload failed — use jpg, png or webp"); }
    setUploadingAsset(false);
  };
  const removeAsset = (path) => setProfile((p) => ({ ...p, assets: (p.assets || []).filter((a) => a.path !== path) }));
  const setAssetRole = (path, role) => setProfile((p) => ({ ...p, assets: (p.assets || []).map((a) => (a.path === path ? { ...a, role } : a)) }));

  const doPlan = async () => {
    setPlanning(true);
    try {
      await planDailyCalendar();
      toast.success("Planning your week — new concepts will appear shortly");
      // poll for up to ~3 min while the background job generates concepts
      let ticks = 0;
      const poll = async () => {
        ticks += 1;
        try {
          const [cal, st] = await Promise.all([getDailyCalendar().catch(() => calendar), getCalendarStatus().catch(() => ({ planning: false }))]);
          setCalendar(cal);
          if (st.planning && ticks < 24) { setTimeout(poll, 8000); }
          else { setPlanning(false); if (!st.planning) toast.success("Your week is planned"); }
        } catch { setPlanning(false); }
      };
      setTimeout(poll, 6000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not plan the week");
      setPlanning(false);
    }
  };
  const doApproveEntry = async (id) => {
    setBusyEntry(id);
    try { await approveCalendarEntry(id); setCalendar((c) => c.map((e) => (e.id === id ? { ...e, status: "approved" } : e))); toast.success("Pre-approved"); }
    catch { toast.error("Could not approve"); }
    setBusyEntry(null);
  };
  const doRegenEntry = async (id) => {
    setBusyEntry(id);
    try { const d = await regenerateCalendarEntry(id); setCalendar((c) => c.map((e) => (e.id === id ? { ...e, concept: d.concept, status: "pending" } : e))); toast.success("Fresh concept"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not regenerate"); }
    setBusyEntry(null);
  };
  const doDeleteEntry = async (id) => {
    const prev = calendar;
    setCalendar((c) => c.filter((e) => e.id !== id));
    try { await deleteCalendarEntry(id); } catch { setCalendar(prev); toast.error("Could not remove"); }
  };
  const doApproveAll = async () => {
    setApprovingAll(true);
    try {
      const d = await approveAllCalendar();
      setCalendar((c) => c.map((e) => (e.status === "pending" ? { ...e, status: "approved" } : e)));
      toast.success(d.approved ? `Approved ${d.approved} concept${d.approved > 1 ? "s" : ""}` : "Nothing pending");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not approve all"); }
    setApprovingAll(false);
  };
  const doAssetPreview = async (path, role) => {
    setPreviewAsset(path);
    try {
      const d = await previewAssetScene(path, role);
      setAssetPreview({ url: mediaUrl(d.path) });
    } catch (e) {
      if (e?.response?.status === 404) {
        setProfile((p) => ({ ...p, assets: (p.assets || []).filter((a) => a.path !== path) }));
        toast.error("That asset is missing — removed it. Please re-upload, then Save brand profile.");
      } else {
        toast.error(e?.response?.data?.detail || "Could not generate preview");
      }
    }
    setPreviewAsset(null);
  };

  const doPreview = async () => {
    setPreviewing(true);
    try {
      const d = await previewDailyConcept();
      setPending({ concept: d.concept, concept_summary: d.concept?.concept_summary });
      toast.success("Fresh concept ready — review it below");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not generate a concept"); }
    setPreviewing(false);
  };

  const doApprove = async () => {
    setApproving(true);
    try {
      const proj = await approveDailyConcept();
      toast.success("Approved — your video is rendering");
      setPending(null);
      navigate(`/video/${proj.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not approve"); }
    setApproving(false);
  };

  const doDiscard = async () => {
    try { await discardDailyConcept(); setPending(null); toast.success("Concept discarded"); }
    catch { toast.error("Could not discard"); }
  };

  const doSaveProfile = async () => {
    setSavingProfile(true);
    try { await saveBrandProfile(profile); toast.success("Brand profile saved"); }
    catch { toast.error("Could not save profile"); }
    setSavingProfile(false);
  };

  const doSaveSettings = async () => {
    if (settings.enabled && !isPro) { toast.error("Daily Video is a Pro feature"); return; }
    setSavingSettings(true);
    try {
      await saveDailySettings({ ...settings, app_origin: window.location.origin });
      toast.success("Schedule saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save settings");
    }
    setSavingSettings(false);
  };

  const doAutofill = async (value) => {
    if (!value) return;
    const [source, id] = value.split(":");
    setAutofilling(true);
    try {
      const r = await dailyAutofill(source, id || null);
      const p = r.profile || {};
      setProfile((cur) => ({
        ...cur,
        business_name: p.business_name || cur.business_name,
        business_description: p.business_description || cur.business_description,
        products_services: p.products_services || cur.products_services,
        target_audience: p.target_audience || cur.target_audience,
      }));
      setSettings((cur) => ({
        ...cur,
        preset_id: r.preset_id || cur.preset_id,
        locked_tone: p.tone || cur.locked_tone,
        instructions: p.hook && !(cur.instructions || "").includes(p.hook) ? `${cur.instructions ? cur.instructions + "\n" : ""}Signature hook idea: ${p.hook}` : cur.instructions,
      }));
      toast.success(`Filled from ${r.source_label} — review, then save the profile and schedule`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not auto-fill");
    }
    setAutofilling(false);
  };

  const doGenerateFromPreset = async () => {
    if (!settings?.preset_id) { toast.error("Pick a preset first"); return; }
    setRunning(true);
    try {
      await saveDailySettings({ ...settings, app_origin: window.location.origin });
      const proj = await runDailyNow();
      toast.success("Generating a fresh video from your preset");
      const h = await getDailyHistory().catch(() => []);
      setHistory(h);
      navigate(`/video/${proj.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate from preset");
    }
    setRunning(false);
  };

  const doRunNow = async () => {
    setRunning(true);
    try {
      const proj = await runDailyNow();
      toast.success("Fresh video is generating — find it in your Library");
      const h = await getDailyHistory().catch(() => []);
      setHistory(h);
      navigate(`/video/${proj.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate today's video");
    }
    setRunning(false);
  };

  return {
    navigate,
    loading, isPro, options, profile, settings, presets, pastVideos, autofilling, remixTarget, setRemixTarget, canTwiceDaily,
    doAutofill, doGenerateFromPreset,
    savingProfile, savingSettings, running, history, uploadingAsset,
    pending, previewing, approving, calendar, planning, busyEntry, approvingAll,
    assetPreview, setAssetPreview, previewAsset,
    setP, setS, handleAssetUpload, removeAsset, setAssetRole,
    doPlan, doApproveEntry, doRegenEntry, doDeleteEntry, doApproveAll, doAssetPreview,
    doPreview, doApprove, doDiscard, doSaveProfile, doSaveSettings, doRunNow,
  };
}
